import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

type PresetKey = 'today' | '7' | '30' | 'this_month' | 'last_month' | 'this_year';

export interface PeriodFilterValue {
  preset: PresetKey | null;
  startDate: string | null; // YYYY-MM-DD
  endDate: string | null;
}

interface PeriodFilterDropdownProps {
  value: PeriodFilterValue;
  onChange: (value: PeriodFilterValue) => void;
}

const PRESETS: { key: PresetKey; labelKey: string }[] = [
  { key: 'today', labelKey: 'dashboard_period_today' },
  { key: '7', labelKey: 'dashboard_period_7days' },
  { key: '30', labelKey: 'dashboard_period_30days' },
  { key: 'this_month', labelKey: 'dashboard_period_this_month' },
  { key: 'last_month', labelKey: 'dashboard_period_last_month' },
  { key: 'this_year', labelKey: 'dashboard_period_this_year' },
];

const DAYS_OF_WEEK = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];

function getDaysInMonth(year: number, month: number): number {
  return new Date(year, month + 1, 0).getDate();
}

function getFirstDayOfWeek(year: number, month: number): number {
  return new Date(year, month, 1).getDay();
}

function formatYMD(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
}

function getPresetLabel(t: (k: string) => string, value: PeriodFilterValue): string {
  if (value.preset) {
    const found = PRESETS.find((p) => p.key === value.preset);
    if (found) return t(found.labelKey);
  }
  if (value.startDate && value.endDate) {
    return `${value.startDate} - ${value.endDate}`;
  }
  return t('dashboard_period_select');
}

export const PeriodFilterDropdown: React.FC<PeriodFilterDropdownProps> = ({ value, onChange }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<PeriodFilterValue>(value);
  const [calYear, setCalYear] = useState(() => new Date().getFullYear());
  const [calMonth, setCalMonth] = useState(() => new Date().getMonth());
  const [pickingStart, setPickingStart] = useState(true);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Sync draft when value changes externally
  useEffect(() => {
    setDraft(value);
  }, [value]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  const handlePresetChange = useCallback((key: PresetKey) => {
    setDraft({ preset: key, startDate: null, endDate: null });
  }, []);

  const handleDayClick = useCallback((day: number) => {
    const clicked = formatYMD(new Date(calYear, calMonth, day));
    setDraft((prev) => {
      if (pickingStart) {
        setPickingStart(false);
        return { preset: null, startDate: clicked, endDate: null };
      }
      // picking end
      setPickingStart(true);
      const start = prev.startDate ?? clicked;
      if (clicked < start) {
        return { preset: null, startDate: clicked, endDate: start };
      }
      return { preset: null, startDate: start, endDate: clicked };
    });
  }, [calYear, calMonth, pickingStart]);

  const handlePrevMonth = useCallback(() => {
    setCalMonth((m) => {
      if (m === 0) {
        setCalYear((y) => y - 1);
        return 11;
      }
      return m - 1;
    });
  }, []);

  const handleNextMonth = useCallback(() => {
    setCalMonth((m) => {
      if (m === 11) {
        setCalYear((y) => y + 1);
        return 0;
      }
      return m + 1;
    });
  }, []);

  const handleSave = useCallback(() => {
    onChange(draft);
    setOpen(false);
  }, [draft, onChange]);

  const calendarDays = useMemo(() => {
    const daysInMonth = getDaysInMonth(calYear, calMonth);
    const firstDay = getFirstDayOfWeek(calYear, calMonth);
    const cells: (number | null)[] = [];
    for (let i = 0; i < firstDay; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(d);
    return cells;
  }, [calYear, calMonth]);

  const monthLabel = useMemo(() => {
    const d = new Date(calYear, calMonth, 1);
    return d.toLocaleString('default', { month: 'long', year: 'numeric' });
  }, [calYear, calMonth]);

  const isDayInRange = useCallback((day: number): boolean => {
    if (!draft.startDate || !draft.endDate) return false;
    const d = formatYMD(new Date(calYear, calMonth, day));
    return d >= draft.startDate && d <= draft.endDate;
  }, [draft, calYear, calMonth]);

  const isDaySelected = useCallback((day: number): boolean => {
    const d = formatYMD(new Date(calYear, calMonth, day));
    return d === draft.startDate || d === draft.endDate;
  }, [draft, calYear, calMonth]);

  const isToday = useCallback((day: number): boolean => {
    const now = new Date();
    return calYear === now.getFullYear() && calMonth === now.getMonth() && day === now.getDate();
  }, [calYear, calMonth]);

  const canSave = draft.preset !== null || (draft.startDate !== null && draft.endDate !== null);

  return (
    <div className="period-dropdown" ref={dropdownRef}>
      <button
        type="button"
        className="period-dropdown-trigger"
        onClick={() => setOpen((o) => !o)}
      >
        {getPresetLabel(t, value)}
        <span className="period-dropdown-arrow">{open ? '▲' : '▼'}</span>
      </button>

      {open && (
        <div className="period-dropdown-panel">
          {/* Presets block */}
          <div className="period-block">
            <div className="period-presets">
              {PRESETS.map((p) => (
                <label key={p.key} className="period-preset-option">
                  <input
                    type="radio"
                    name="period-preset"
                    checked={draft.preset === p.key}
                    onChange={() => handlePresetChange(p.key)}
                  />
                  <span className="period-preset-radio" />
                  <span className="period-preset-label">{t(p.labelKey)}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Calendar block */}
          <div className="period-block">
            <div className="period-calendar">
              <div className="period-calendar-header">
                <button type="button" className="period-calendar-nav" onClick={handlePrevMonth}>‹</button>
                <span className="period-calendar-month">{monthLabel}</span>
                <button type="button" className="period-calendar-nav" onClick={handleNextMonth}>›</button>
              </div>
              <div className="period-calendar-weekdays">
                {DAYS_OF_WEEK.map((d) => (
                  <span key={d} className="period-calendar-weekday">{d}</span>
                ))}
              </div>
              <div className="period-calendar-grid">
                {calendarDays.map((day, i) =>
                  day === null ? (
                    <span key={`empty-${i}`} className="period-calendar-cell period-calendar-cell--empty" />
                  ) : (
                    <button
                      key={day}
                      type="button"
                      className={[
                        'period-calendar-cell',
                        isDaySelected(day) ? 'period-calendar-cell--selected' : '',
                        isDayInRange(day) ? 'period-calendar-cell--in-range' : '',
                        isToday(day) ? 'period-calendar-cell--today' : '',
                      ].join(' ')}
                      onClick={() => handleDayClick(day)}
                    >
                      {day}
                    </button>
                  )
                )}
              </div>

              {/* Save button inside calendar block */}
              <div className="period-save-row">
                <button
                  type="button"
                  className="period-save-btn"
                  disabled={!canSave}
                  onClick={handleSave}
                >
                  {t('dashboard_period_save')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
